'use client';

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { RequestContext } from '@impact/request-context';

export interface SessionContextValue<TSession = unknown> {
  session: TSession | null;
  context: RequestContext;
}

const SessionContext = createContext<SessionContextValue<unknown> | undefined>(undefined);

export interface SessionProviderProps<TSession> {
  value: SessionContextValue<TSession>;
  children: ReactNode;
}

export function SessionProvider<TSession>({ value, children }: SessionProviderProps<TSession>) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession<TSession = unknown>(): SessionContextValue<TSession> {
  const value = useContext(SessionContext);

  if (!value) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return value as SessionContextValue<TSession>;
}
